/**
 * User Repository Implementation
 * Implements the UserRepository interface
 * Coordinates between API calls and mappers
 */

import { UserRepository } from "../../domain/repositories/UserRepository";
import { UserEntity } from "../../domain/entities/UserEntity";
import {
  getUsers,
  getUserById as getUserByIdApi,
  getAssignableManagers,
  createUserWithPermissions,
  updateUserPermissions,
  updateUserManager,
  deleteUser as deleteUserApi,
} from "../api/usersApi";
import { UserMapper } from "../mappers/UserMapper";
import {
  UsersResponseDTO,
  AssignableManagersResponseDTO,
} from "../models/UserDTOs";

export class UserRepositoryImpl implements UserRepository {
  async getUsers(): Promise<UserEntity[]> {
    const response = (await getUsers()) as UsersResponseDTO;
    return UserMapper.toUserEntityArray(response);
  }

  async getUserById(userId: number): Promise<UserEntity> {
    const response = await getUserByIdApi(userId);
    // Response structure: { success: true, data: UserWithPermissionsDTO }
    if (response.success && response.data) {
      return UserMapper.toUserEntity(response.data);
    }
    throw new Error("User not found");
  }

  async getAssignableManagers(): Promise<
    Array<{ id: number; email: string; userName?: string; role: string }>
  > {
    const response =
      (await getAssignableManagers()) as AssignableManagersResponseDTO;
    return UserMapper.toAssignableManagersArray(response);
  }

  async createUserWithPermissions(data: {
    email: string;
    userName?: string;
    password: string;
    role_id: number;
    manager_id?: number;
  }): Promise<UserEntity> {
    const response = await createUserWithPermissions(data);
    // Response structure may vary - adjust based on actual API response
    if (response.data?.user) {
      return UserMapper.toUserEntity(response.data.user);
    }
    // If response doesn't include user data, fetch it
    const users = await this.getUsers();
    const newUser = users.find((u) => u.email === data.email);
    if (!newUser) {
      throw new Error("User created but could not be retrieved");
    }
    return newUser;
  }

  async updateUserPermissions(
    userId: number,
    permissions: Record<string, Record<string, boolean>>
  ): Promise<void> {
    await updateUserPermissions(userId, permissions);
  }

  async updateUserManager(
    userId: number,
    managerId: number | null
  ): Promise<void> {
    await updateUserManager(userId, managerId);
  }

  async deleteUser(userId: number): Promise<void> {
    await deleteUserApi(userId);
  }
}
